import {
  Activity,
  AlertTriangle,
  ArrowDownUp,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  Cloud,
  CloudOff,
  Goal,
  MapPin,
  NotebookPen,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Save,
  ShieldAlert,
  Square,
  Star,
  Swords,
  ThermometerSun,
  UserRound,
  Users,
  X,
  Undo2,
  MonitorUp,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ClubLogo } from './ClubLogo';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { PREMIERSHIP_CLUBS, resolveBundledClub } from './leagueClubs';
import { getSupabase } from './supabase';
import { POLONIA_2026_27_FIXTURES, officialOpponent, officialVenueLabel } from './officialFixtures';
import { LivePlayerPerformancePanel } from './LivePlayerPerformancePanel';
import { LiveVoiceLogger } from './LiveVoiceLogger';
import type {
  LiveMatchEvent,
  LiveMatchEventType,
  LiveMatchState,
  LiveMatchStats,
  LivePlayerPerformance,
  Match,
  Player,
} from './types';

type ViewMode = 'setup' | 'live' | 'history';

const DEFAULT_STATS: LiveMatchStats = {
  possession: 50,
  shots: 0,
  shots_on_target: 0,
  corners: 0,
  fouls: 0,
  offsides: 0,
  passes: 0,
  pass_accuracy: 0,
};

const EMPTY_STATE: Omit<LiveMatchState, 'match_id' | 'team_id'> = {
  phase: 'not_started',
  minute: 0,
  seconds: 0,
  running: false,
  home_score: 0,
  away_score: 0,
  venue: 'Home',
  referee: '',
  weather: '',
  attendance: '',
  formation: '4-3-3',
  coach_notes: '',
};

const FORMATION_SLOTS: Record<string, string[][]> = {
  '4-3-3': [['LW', 'ST', 'RW'], ['LCM', 'CM', 'RCM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  '4-2-3-1': [['ST'], ['LW', 'CAM', 'RW'], ['LDM', 'RDM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  '4-4-2': [['LST', 'RST'], ['LM', 'LCM', 'RCM', 'RM'], ['LB', 'LCB', 'RCB', 'RB'], ['GK']],
  '3-5-2': [['LST', 'RST'], ['LWB', 'LCM', 'CAM', 'RCM', 'RWB'], ['LCB', 'CB', 'RCB'], ['GK']],
};

function storageKey(prefix: string, matchId: string) {
  return `ecc_live_${prefix}_${matchId}`;
}

function loadLocal<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;

    const parsed: unknown = JSON.parse(raw);

    // Arrays must stay iterable. Older V23 builds accidentally converted stored
    // event arrays into numeric-key objects via object spread. Recover those
    // values as well so existing devices do not need their local data cleared.
    if (Array.isArray(fallback)) {
      if (Array.isArray(parsed)) return parsed as T;
      if (parsed && typeof parsed === 'object') {
        return Object.keys(parsed as Record<string, unknown>)
          .filter((key) => /^\d+$/.test(key))
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => (parsed as Record<string, unknown>)[key]) as T;
      }
      return fallback;
    }

    if (fallback && typeof fallback === 'object' && parsed && typeof parsed === 'object') {
      return { ...(fallback as Record<string, unknown>), ...(parsed as Record<string, unknown>) } as T;
    }

    return parsed as T;
  } catch {
    return fallback;
  }
}

function eventIcon(type: LiveMatchEventType) {
  if (type === 'goal' || type === 'assist') return Goal;
  if (type === 'yellow' || type === 'red') return Square;
  if (type === 'substitution') return ArrowDownUp;
  if (type === 'injury') return AlertTriangle;
  if (type === 'period') return Clock3;
  return NotebookPen;
}

function phaseLabel(
  phase: LiveMatchState['phase'],
  t: ReturnType<typeof useI18n>['t'],
) {
  const labels = {
    not_started: t.notStarted,
    first_half: t.firstHalf,
    half_time: t.halfTime,
    second_half: t.secondHalf,
    full_time: t.fullTime,
  };
  return labels[phase];
}


function formatHistoricalMinute(minute: number) {
  const value = Math.max(0, Math.round(Number(minute) || 0));
  return value > 90 ? `90+${value - 90}'` : `${value}'`;
}

function digitsOnlyMinute(raw: string) {
  const digits = raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (!digits) return 0;
  return Math.min(120, Math.max(0, Number(digits)));
}

function encodeEventNote(event: LiveMatchEvent) {
  return JSON.stringify({ title: event.title, detail: event.detail || null, side: event.side || 'home' });
}

function decodeCloudEvent(row: Record<string, unknown>): LiveMatchEvent {
  let meta: { title?: string; detail?: string | null; side?: 'home' | 'away' } = {};
  if (typeof row.note === 'string' && row.note) {
    try {
      const parsed = JSON.parse(row.note);
      if (parsed && typeof parsed === 'object') meta = parsed;
    } catch {
      meta.detail = row.note;
    }
  }
  const eventType = String(row.event_type || 'note') as LiveMatchEventType;
  return {
    id: String(row.id),
    match_id: String(row.match_id),
    team_id: String(row.team_id),
    minute: Number(row.minute || 0),
    event_type: eventType,
    player_id: row.player_id ? String(row.player_id) : null,
    secondary_player_id: row.secondary_player_id ? String(row.secondary_player_id) : null,
    side: meta.side === 'away' ? 'away' : 'home',
    title: meta.title || eventType.replaceAll('_', ' '),
    detail: meta.detail || null,
    created_at: row.created_at ? String(row.created_at) : undefined,
  };
}

export function ProfessionalMatchCentre() {
  const { t, language } = useI18n();
  const { team, players, matches, lineups, loading, error, createMatch, saveLineup, refresh } = useData();

  const [view, setView] = useState<ViewMode>(() => {
    try {
      const requested = localStorage.getItem('ecc_match_centre_requested_view');
      return requested === 'live' || requested === 'history' || requested === 'setup'
        ? requested
        : 'setup';
    } catch {
      return 'setup';
    }
  });
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [date, setDate] = useState('');
  const [opponent, setOpponent] = useState('');
  const [competition, setCompetition] = useState('Only Sport Ltd Premiership');
  const [createMessage, setCreateMessage] = useState('');
  const [officialDraftRound, setOfficialDraftRound] = useState<number | null>(null);

  const selectedMatch = useMemo(
    () => matches.find((match) => match.id === selectedMatchId) || null,
    [matches, selectedMatchId],
  );

  const orderedMatches = useMemo(
    () => [...matches].sort((a, b) => b.match_date.localeCompare(a.match_date)),
    [matches],
  );

  const [liveState, setLiveState] = useState<LiveMatchState | null>(null);
  const [stats, setStats] = useState<LiveMatchStats>(DEFAULT_STATS);
  const [events, setEvents] = useState<LiveMatchEvent[]>([]);
  const [playerPerformance, setPlayerPerformance] = useState<Record<string, LivePlayerPerformance>>({});
  const [slots, setSlots] = useState<Record<string, string | null>>({});
  const [cloudStatus, setCloudStatus] = useState<'unknown' | 'synced' | 'pending' | 'local'>('unknown');
  const [online, setOnline] = useState(() => navigator.onLine);
  const [syncTrackingReady, setSyncTrackingReady] = useState(false);
  const [lastCloudSync, setLastCloudSync] = useState('');
  const [eventPlayer, setEventPlayer] = useState('');
  const [eventSecondaryPlayer, setEventSecondaryPlayer] = useState('');
  const [eventDetail, setEventDetail] = useState('');
  const [activeEventType, setActiveEventType] = useState<LiveMatchEventType | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const intervalRef = useRef<number | null>(null);
  const wakeLockRef = useRef<{ release: () => Promise<void>; released?: boolean } | null>(null);
  const [screenAwake, setScreenAwake] = useState(false);
  const [safetyMessage, setSafetyMessage] = useState('');
  const [historicalEditMode, setHistoricalEditMode] = useState(false);
  const [historicalEventMinute, setHistoricalEventMinute] = useState(0);
  const [motmPlayerId, setMotmPlayerId] = useState('');

  useEffect(() => {
    try { localStorage.removeItem('ecc_match_centre_requested_view'); } catch {}
  }, []);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => {
      setOnline(false);
      setCloudStatus((current) => current === 'unknown' ? 'local' : current);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  async function requestScreenAwake() {
    try {
      if (wakeLockRef.current && !wakeLockRef.current.released) {
        setScreenAwake(true);
        setSafetyMessage(t.screenAwakeActive);
        return;
      }
      const nav = navigator as Navigator & {
        wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void>; released?: boolean }> };
      };
      if (!nav.wakeLock?.request) {
        setSafetyMessage(t.screenAwakeUnavailable);
        return;
      }
      wakeLockRef.current = await nav.wakeLock.request('screen');
      setScreenAwake(true);
      setSafetyMessage(t.screenAwakeActive);
    } catch {
      setScreenAwake(false);
      setSafetyMessage(t.screenAwakeFailed);
    }
  }

  async function releaseScreenAwake() {
    try {
      await wakeLockRef.current?.release();
    } catch {
      // Already released or unavailable.
    }
    wakeLockRef.current = null;
    setScreenAwake(false);
    setSafetyMessage(t.screenAwakeOff);
  }

  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible' && screenAwake && (!wakeLockRef.current || wakeLockRef.current.released)) {
        void requestScreenAwake();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      void wakeLockRef.current?.release().catch(() => undefined);
      wakeLockRef.current = null;
    };
  }, [screenAwake]);

  useEffect(() => {
    if (liveState?.phase === 'full_time' && screenAwake) {
      void releaseScreenAwake();
    }
  }, [liveState?.phase, screenAwake]);

  useEffect(() => {
    if (!selectedMatch || !team) return;
    const baseState: LiveMatchState = {
      ...EMPTY_STATE,
      match_id: selectedMatch.id,
      team_id: team.id,
      home_score: Number(selectedMatch.goals_for ?? 0),
      away_score: Number(selectedMatch.goals_against ?? 0),
      phase:
        selectedMatch.status === 'full_time'
          ? 'full_time'
          : selectedMatch.status === 'live'
            ? 'first_half'
            : 'not_started',
      minute: selectedMatch.status === 'full_time' ? Math.max(90, Number(selectedMatch.live_minute ?? 0)) : Number(selectedMatch.live_minute ?? 0),
    };
    setLiveState(loadLocal(storageKey('state', selectedMatch.id), baseState));
    setStats(loadLocal(storageKey('stats', selectedMatch.id), DEFAULT_STATS));
    setEvents(loadLocal(storageKey('events', selectedMatch.id), [] as LiveMatchEvent[]));
    setPlayerPerformance(loadLocal(storageKey('performance', selectedMatch.id), {} as Record<string, LivePlayerPerformance>));
    setSlots(loadLocal(storageKey('slots', selectedMatch.id), {} as Record<string, string | null>));
    setMotmPlayerId(selectedMatch.mvp_player_id || '');
    setHistoricalEventMinute(0);
    setCloudStatus('unknown');
    setSyncTrackingReady(false);
    try {
      setLastCloudSync(localStorage.getItem(storageKey('last-cloud-sync', selectedMatch.id)) || '');
    } catch {
      setLastCloudSync('');
    }

    const client = getSupabase();
    if (!client) {
      setCloudStatus('local');
      setSyncTrackingReady(true);
      return;
    }

    void Promise.all([
      client.from('match_live_state').select('*').eq('match_id', selectedMatch.id).maybeSingle(),
      client.from('match_live_stats').select('*').eq('match_id', selectedMatch.id).maybeSingle(),
      client.from('match_events').select('*').eq('match_id', selectedMatch.id).order('minute'),
      client.from('contributions').select('*').eq('match_id', selectedMatch.id),
    ]).then(([stateResult, statsResult, eventsResult, contributionsResult]) => {
      const tablesAvailable =
        !stateResult.error &&
        !statsResult.error &&
        !eventsResult.error &&
        !contributionsResult.error;

      if (!tablesAvailable) {
        setCloudStatus('local');
        setSyncTrackingReady(true);
        return;
      }

      if (stateResult.data) {
        const cloudState = stateResult.data as LiveMatchState;
        setLiveState((current) => ({ ...(current || baseState), ...cloudState, minute: cloudState.phase === 'full_time' ? Math.max(90, Number(cloudState.minute || 0)) : Number(cloudState.minute || 0), running: false }));
      }
      if (statsResult.data) {
        const row = statsResult.data as LiveMatchStats & { match_id?: string; team_id?: string };
        setStats({
          possession: Number(row.possession ?? 50),
          shots: Number(row.shots ?? 0),
          shots_on_target: Number(row.shots_on_target ?? 0),
          corners: Number(row.corners ?? 0),
          fouls: Number(row.fouls ?? 0),
          offsides: Number(row.offsides ?? 0),
          passes: Number(row.passes ?? 0),
          pass_accuracy: Number(row.pass_accuracy ?? 0),
        });
      }
      if (eventsResult.data?.length) setEvents((eventsResult.data as Record<string, unknown>[]).map(decodeCloudEvent));
      if (contributionsResult.data?.length) {
        setPlayerPerformance((current) => {
          const next = { ...current };
          (contributionsResult.data as Array<Record<string, unknown>>).forEach((row) => {
            const playerId = String(row.player_id || '');
            const player = players.find((candidate) => candidate.id === playerId);
            if (!playerId || !player) return;
            const minutes = Number(row.minutes || 0);
            const starter = String(row.status || '') === 'starter';
            const existing = next[playerId];
            next[playerId] = {
              match_id: selectedMatch.id, team_id: team.id, player_id: playerId,
              started: starter, on_pitch: false, minute_on: starter ? 0 : (existing?.minute_on ?? Math.max(0, 90 - minutes)),
              minute_off: starter && minutes < 90 ? minutes : existing?.minute_off ?? null,
              rating: Number(row.rating ?? existing?.rating ?? player.current_form ?? 6),
              goals: Number(row.goals ?? existing?.goals ?? 0), assists: Number(row.assists ?? existing?.assists ?? 0),
              yellow_cards: Number(row.yellow ?? existing?.yellow_cards ?? 0), red_cards: Number(row.red ?? existing?.red_cards ?? 0),
              shots: existing?.shots ?? 0, shots_on_target: existing?.shots_on_target ?? 0, key_passes: existing?.key_passes ?? 0,
              touches: existing?.touches ?? 0, duels_won: existing?.duels_won ?? 0, duels_lost: existing?.duels_lost ?? 0,
              tackles_won: existing?.tackles_won ?? 0, tackles_lost: existing?.tackles_lost ?? 0, interceptions: existing?.interceptions ?? 0,
              clearances: existing?.clearances ?? 0, headers_won: existing?.headers_won ?? 0, headers_lost: existing?.headers_lost ?? 0,
              fouls_won: existing?.fouls_won ?? 0, fouls_committed: existing?.fouls_committed ?? 0, offsides: existing?.offsides ?? 0,
              saves: existing?.saves ?? 0, ball_losses: existing?.ball_losses ?? 0, notes: existing?.notes ?? '',
            };
          });
          return next;
        });
      }
      setCloudStatus('synced');
      window.setTimeout(() => setSyncTrackingReady(true), 0);
    }).catch(() => {
      setCloudStatus('local');
      setSyncTrackingReady(true);
    });
  }, [selectedMatch, team]);

  useEffect(() => {
    if (!liveState) return;
    localStorage.setItem(storageKey('state', liveState.match_id), JSON.stringify(liveState));
  }, [liveState]);

  useEffect(() => {
    if (!selectedMatch) return;
    localStorage.setItem(storageKey('stats', selectedMatch.id), JSON.stringify(stats));
  }, [stats, selectedMatch]);

  useEffect(() => {
    if (!selectedMatch) return;
    localStorage.setItem(storageKey('events', selectedMatch.id), JSON.stringify(events));
  }, [events, selectedMatch]);

  useEffect(() => {
    if (!selectedMatch) return;
    localStorage.setItem(storageKey('slots', selectedMatch.id), JSON.stringify(slots));
  }, [slots, selectedMatch]);

  useEffect(() => {
    if (!selectedMatch) return;
    localStorage.setItem(storageKey('performance', selectedMatch.id), JSON.stringify(playerPerformance));
  }, [playerPerformance, selectedMatch]);

  useEffect(() => {
    if (!syncTrackingReady || !selectedMatch) return;
    setCloudStatus((current) => current === 'local' ? 'local' : 'pending');
  }, [liveState, stats, events, playerPerformance, slots, syncTrackingReady, selectedMatch]);

  useEffect(() => {
    if (!liveState?.running || liveState.phase === 'full_time' || historicalEditMode) {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    intervalRef.current = window.setInterval(() => {
      setLiveState((current) => {
        if (!current || !current.running) return current;
        let seconds = current.seconds + 1;
        let minute = current.minute;
        if (seconds >= 60) {
          seconds = 0;
          minute += 1;
        }
        return { ...current, minute, seconds };
      });
    }, 1000);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [liveState?.running, liveState?.phase, historicalEditMode]);

  useEffect(() => {
    if (!selectedMatchId && matches.length) {
      const upcomingOrLatest =
        matches.find((match) => match.status === 'live') ||
        [...matches]
          .filter((match) => new Date(match.match_date).getTime() >= Date.now() - 6 * 60 * 60 * 1000)
          .sort((a, b) => a.match_date.localeCompare(b.match_date))[0] ||
        orderedMatches[0];
      if (upcomingOrLatest) setSelectedMatchId(upcomingOrLatest.id);
    }
  }, [matches, orderedMatches, selectedMatchId]);

  function useOfficialFixture(round: number) {
    const fixture = POLONIA_2026_27_FIXTURES.find((item) => item.round === round);
    if (!fixture) return;
    setOpponent(officialOpponent(fixture));
    setCompetition(fixture.competition);
    // Official GDSFC post currently provides the date, but no public kick-off time.
    // Midnight is intentionally used as an incomplete marker and cannot be saved.
    setDate(`${fixture.date}T00:00`);
    setOfficialDraftRound(round);
    setShowCreate(true);
    setCreateMessage(t.setKickoffBeforeSaving);
  }

  async function submitMatch() {
    setCreateMessage('');
    if (!date || !opponent) {
      setCreateMessage(t.requiredFields);
      return;
    }
    if (officialDraftRound && date.endsWith('T00:00')) {
      setCreateMessage(t.setKickoffBeforeSaving);
      return;
    }
    const result = await createMatch({ match_date: date, opponent, competition });
    setCreateMessage(result.ok ? t.matchCreated : result.message);
    if (result.ok) {
      setShowCreate(false);
      setDate('');
      setOpponent('');
      setOfficialDraftRound(null);
      await refresh();
    }
  }

  function updateState(patch: Partial<LiveMatchState>) {
    setLiveState((current) => (current ? { ...current, ...patch } : current));
  }

  function changeScore(side: 'home' | 'away', delta: number) {
    setLiveState((current) => {
      if (!current) return current;
      const key = side === 'home' ? 'home_score' : 'away_score';
      return { ...current, [key]: Math.max(0, current[key] + delta) };
    });
  }

  const activeMatchMinute = historicalEditMode ? historicalEventMinute : (liveState?.minute ?? 0);

  function setHistoricalMinute(value: number) {
    setHistoricalEventMinute(Math.min(120, Math.max(0, Math.round(Number(value) || 0))));
  }

  function updateEventMinute(eventId: string, value: number) {
    const minute = Math.min(120, Math.max(0, Math.round(Number(value) || 0)));
    setEvents((current) => current.map((event) => event.id === eventId ? { ...event, minute } : event));
  }

  function addEvent(type: LiveMatchEventType, side: 'home' | 'away' = 'home') {
    if (!liveState || !selectedMatch || !team) return;
    setActiveEventType(type);
    if (type === 'period' || type === 'note') {
      const title = type === 'period' ? phaseLabel(liveState.phase, t) : t.coachNote;
      const event: LiveMatchEvent = {
        id: crypto.randomUUID(),
        match_id: selectedMatch.id,
        team_id: team.id,
        minute: activeMatchMinute,
        event_type: type,
        side,
        title,
        detail: eventDetail || null,
        created_at: new Date().toISOString(),
      };
      setEvents((current) => [...current, event]);
      setEventDetail('');
      setActiveEventType(null);
    }
  }

  function confirmPlayerEvent() {
    if (!activeEventType || !liveState || !selectedMatch || !team) return;
    const player = players.find((candidate) => candidate.id === eventPlayer);
    const secondary = players.find((candidate) => candidate.id === eventSecondaryPlayer);
    const eventTitleMap: Partial<Record<LiveMatchEventType, string>> = {
      goal: t.goal,
      assist: t.assist,
      yellow: t.yellowCard,
      red: t.redCard,
      substitution: t.substitution,
      injury: t.injury,
      note: t.coachNote,
      period: t.period,
    };
    const event: LiveMatchEvent = {
      id: crypto.randomUUID(),
      match_id: selectedMatch.id,
      team_id: team.id,
      minute: activeMatchMinute,
      event_type: activeEventType,
      player_id: eventPlayer || null,
      secondary_player_id: eventSecondaryPlayer || null,
      side: 'home',
      title: player ? `${eventTitleMap[activeEventType] || activeEventType} · ${player.name}` : (eventTitleMap[activeEventType] || activeEventType),
      detail:
        activeEventType === 'substitution' && secondary
          ? `${player?.name || '—'} → ${secondary.name}`
          : eventDetail || null,
      created_at: new Date().toISOString(),
    };
    setEvents((current) => [...current, event]);
    if (activeEventType === 'goal') changeScore('home', 1);
    setEventPlayer('');
    setEventSecondaryPlayer('');
    setEventDetail('');
    setActiveEventType(null);
  }

  function recordVoiceCommand(player: Player, type: LiveMatchEventType, transcript: string) {
    if (!liveState || !selectedMatch || !team) return;

    const labels: Partial<Record<LiveMatchEventType, string>> = {
      goal: 'Goal', assist: 'Assist', shot: 'Shot', shot_on_target: 'Shot on target',
      key_pass: 'Key pass', touch: 'Touch', duel_won: 'Duel won', duel_lost: 'Duel lost',
      tackle_won: 'Tackle won', tackle_lost: 'Tackle lost', interception: 'Interception',
      clearance: 'Clearance', header_won: 'Header won', header_lost: 'Header lost',
      foul_won: 'Foul won', foul_committed: 'Foul committed', offside: 'Offside',
      save: 'Save', ball_lost: 'Ball lost', yellow: 'Yellow card', red: 'Red card',
      injury: 'Injury',
    };

    const event: LiveMatchEvent = {
      id: crypto.randomUUID(),
      match_id: selectedMatch.id,
      team_id: team.id,
      minute: activeMatchMinute,
      event_type: type,
      player_id: player.id,
      side: 'home',
      title: `${labels[type] || type} · ${player.name}`,
      detail: `Voice: ${transcript}`,
      created_at: new Date().toISOString(),
    };
    setEvents((current) => [...current, event]);

    const perfKey: Partial<Record<LiveMatchEventType, keyof LivePlayerPerformance>> = {
      goal: 'goals', assist: 'assists', shot: 'shots', shot_on_target: 'shots_on_target',
      key_pass: 'key_passes', touch: 'touches', duel_won: 'duels_won', duel_lost: 'duels_lost',
      tackle_won: 'tackles_won', tackle_lost: 'tackles_lost', interception: 'interceptions',
      clearance: 'clearances', header_won: 'headers_won', header_lost: 'headers_lost',
      foul_won: 'fouls_won', foul_committed: 'fouls_committed', offside: 'offsides',
      save: 'saves', ball_lost: 'ball_losses', yellow: 'yellow_cards', red: 'red_cards',
    };

    const key = perfKey[type];
    setPlayerPerformance((current) => {
      const existing = current[player.id] || {
        match_id: selectedMatch.id, team_id: team.id, player_id: player.id,
        started: false, on_pitch: false, minute_on: 0, minute_off: null,
        rating: Number(player.current_form ?? 6), goals: 0, assists: 0,
        yellow_cards: 0, red_cards: 0, notes: '',
      };
      if (!key) return { ...current, [player.id]: existing };
      return {
        ...current,
        [player.id]: { ...existing, [key]: Number(existing[key] ?? 0) + 1 },
      };
    });

    if (type === 'goal') changeScore('home', 1);
    if (type === 'shot' || type === 'shot_on_target') {
      setStats((current) => ({
        ...current,
        shots: current.shots + 1,
        shots_on_target: current.shots_on_target + (type === 'shot_on_target' ? 1 : 0),
      }));
    }
    if (type === 'offside') setStats((current) => ({ ...current, offsides: current.offsides + 1 }));
    if (type === 'foul_committed') setStats((current) => ({ ...current, fouls: current.fouls + 1 }));
    setSafetyMessage(`${player.name} · ${(labels[type] || type)} ✓`);
  }

  function undoLastEvent() {
    setEvents((current) => {
      if (!current.length) {
        setSafetyMessage(t.nothingToUndo);
        return current;
      }

      const last = current[current.length - 1];
      if (last.event_type === 'goal' && last.side === 'home') {
        setLiveState((state) =>
          state ? { ...state, home_score: Math.max(0, state.home_score - 1) } : state,
        );
      }
      if (last.event_type === 'goal' && last.side === 'away') {
        setLiveState((state) =>
          state ? { ...state, away_score: Math.max(0, state.away_score - 1) } : state,
        );
      }

      setSafetyMessage(`${t.lastEventRemoved}: ${last.title}`);
      return current.slice(0, -1);
    });
  }

  async function syncLiveMatch() {
    if (!liveState || !selectedMatch || !team) return;
    setSaveMessage(t.saving);

    if (!navigator.onLine) {
      setOnline(false);
      setCloudStatus('local');
      setSaveMessage(t.offlineChangesProtected);
      return;
    }

    const client = getSupabase();
    if (!client) {
      setCloudStatus('local');
      setSaveMessage(t.savedOnDevice);
      return;
    }

    const normalizedMinute = liveState.phase === 'full_time' ? Math.max(90, Number(liveState.minute || 0)) : Number(liveState.minute || 0);
    const statePayload = { ...liveState, minute: normalizedMinute, running: false, updated_at: new Date().toISOString() };
    const statsPayload = {
      match_id: selectedMatch.id,
      team_id: team.id,
      ...stats,
      updated_at: new Date().toISOString(),
    };

    const [matchResult, stateResult, statsResult] = await Promise.all([
      client
        .from('matches')
        .update({
          goals_for: liveState.home_score,
          goals_against: liveState.away_score,
          status: liveState.phase === 'full_time' ? 'full_time' : liveState.phase === 'not_started' ? 'scheduled' : 'live',
          live_minute: normalizedMinute,
          venue: liveState.venue || null,
          referee: liveState.referee || null,
          weather: liveState.weather || null,
          formation: liveState.formation || null,
          final_notes: liveState.coach_notes || null,
          mvp_player_id: motmPlayerId || null,
        })
        .eq('id', selectedMatch.id),
      client.from('match_live_state').upsert(statePayload, { onConflict: 'match_id' }),
      client.from('match_live_stats').upsert(statsPayload, { onConflict: 'match_id' }),
    ]);

    const eventPayload = events.map((event) => ({
      id: event.id,
      team_id: event.team_id,
      match_id: event.match_id,
      minute: event.minute,
      event_type: event.event_type,
      player_id: event.player_id || null,
      secondary_player_id: event.secondary_player_id || null,
      note: encodeEventNote(event),
      created_at: event.created_at || new Date().toISOString(),
    }));
    const clearEventsResult = await client.from('match_events').delete().eq('match_id', selectedMatch.id);
    const eventResult = clearEventsResult.error
      ? clearEventsResult
      : eventPayload.length
        ? await client.from('match_events').insert(eventPayload)
        : { error: null };

    const contributionPayload = Object.values(playerPerformance)
      .filter((performance) =>
        performance.started ||
        performance.on_pitch ||
        performance.goals > 0 ||
        performance.assists > 0 ||
        performance.yellow_cards > 0 ||
        performance.red_cards > 0 ||
        performance.minute_off !== null
      )
      .map((performance) => {
        const minutes = performance.minute_off !== null
          ? Math.max(0, performance.minute_off - performance.minute_on)
          : liveState.phase === 'full_time'
            ? Math.max(0, 90 - performance.minute_on)
            : Math.max(0, liveState.minute - performance.minute_on);

        return {
          team_id: team.id,
          match_id: selectedMatch.id,
          player_id: performance.player_id,
          minutes,
          rating: performance.rating,
          goals: performance.goals,
          assists: performance.assists,
          status: performance.started ? 'starter' : performance.on_pitch || performance.minute_off !== null ? 'substitute' : 'bench',
          yellow: performance.yellow_cards,
          red: performance.red_cards,
        };
      });

    const contributionResult = contributionPayload.length
      ? await client.from('contributions').upsert(contributionPayload, { onConflict: 'match_id,player_id' })
      : { error: null };

    if (matchResult.error) {
      setCloudStatus('local');
      setSaveMessage(`${matchResult.error.message} · ${t.localChangesProtected}`);
      return;
    }

    if (stateResult.error || statsResult.error || eventResult.error || contributionResult.error) {
      setCloudStatus('local');
      setSaveMessage(t.savedOnDeviceMigrationNeeded);
      return;
    }

    const syncedAt = new Date().toISOString();
    setSyncTrackingReady(false);
    setCloudStatus('synced');
    setLastCloudSync(syncedAt);
    try {
      localStorage.setItem(storageKey('last-cloud-sync', selectedMatch.id), syncedAt);
    } catch {}
    setSaveMessage(t.cloudSynced);
    await refresh();
    window.setTimeout(() => setSyncTrackingReady(true), 0);
  }

  function recordSubstitution(playerOutId: string, playerInId: string) {
    if (!liveState || !selectedMatch || !team) return;
    const playerOut = players.find(player => player.id === playerOutId);
    const playerIn = players.find(player => player.id === playerInId);
    setEvents(current => [...current, {
      id: crypto.randomUUID(), match_id: selectedMatch.id, team_id: team.id,
      minute: activeMatchMinute, event_type: 'substitution',
      player_id: playerOutId, secondary_player_id: playerInId, side: 'home',
      title: t.substitution, detail: `${playerOut?.name || '—'} → ${playerIn?.name || '—'}`,
      created_at: new Date().toISOString(),
    }]);
  }

  function syncStartingPerformance(nextSlots: Record<string, string | null>) {
    const starterIds = new Set(Object.values(nextSlots).filter(Boolean) as string[]);
    setPlayerPerformance(current => {
      const next = { ...current };
      players.forEach(player => {
        const existing = next[player.id];
        const starter = starterIds.has(player.id);
        next[player.id] = {
          match_id: selectedMatch?.id || '',
          team_id: team?.id || '',
          player_id: player.id,
          started: starter,
          on_pitch: starter,
          minute_on: starter ? 0 : (existing?.minute_on ?? 0),
          minute_off: starter ? null : existing?.minute_off ?? null,
          rating: existing?.rating ?? Number(player.current_form ?? 6),
          goals: existing?.goals ?? 0,
          assists: existing?.assists ?? 0,
          yellow_cards: existing?.yellow_cards ?? 0,
          red_cards: existing?.red_cards ?? 0,
          shots: existing?.shots ?? 0,
          shots_on_target: existing?.shots_on_target ?? 0,
          key_passes: existing?.key_passes ?? 0,
          touches: existing?.touches ?? 0,
          duels_won: existing?.duels_won ?? 0,
          duels_lost: existing?.duels_lost ?? 0,
          tackles_won: existing?.tackles_won ?? 0,
          tackles_lost: existing?.tackles_lost ?? 0,
          interceptions: existing?.interceptions ?? 0,
          clearances: existing?.clearances ?? 0,
          headers_won: existing?.headers_won ?? 0,
          headers_lost: existing?.headers_lost ?? 0,
          fouls_won: existing?.fouls_won ?? 0,
          fouls_committed: existing?.fouls_committed ?? 0,
          offsides: existing?.offsides ?? 0,
          saves: existing?.saves ?? 0,
          ball_losses: existing?.ball_losses ?? 0,
          notes: existing?.notes ?? '',
        };
      });
      return next;
    });
  }

  function assignStartingPlayer(slot: string, playerId: string) {
    setSlots(current => {
      const next: Record<string, string | null> = { ...current };

      // A player can only occupy one starting position.
      if (playerId) {
        Object.keys(next).forEach(key => {
          if (next[key] === playerId) next[key] = null;
        });
      }

      next[slot] = playerId || null;
      syncStartingPerformance(next);
      return next;
    });
  }

  function clearStartingXI() {
    const empty = Object.fromEntries(selectedFormationRows.flat().map(slot => [slot, null])) as Record<string,string|null>;
    setSlots(empty);
    syncStartingPerformance(empty);
  }

  function changeHistoricalFormation(formation: string) {
    const rows = FORMATION_SLOTS[formation] || FORMATION_SLOTS['4-3-3'];
    const allowed = new Set(rows.flat());
    const next: Record<string,string|null> = {};

    rows.flat().forEach(slot => {
      next[slot] = allowed.has(slot) ? (slots[slot] || null) : null;
    });

    setSlots(next);
    syncStartingPerformance(next);
    updateState({ formation });
  }

  async function confirmHistoricalStartingXI() {
    if (!selectedMatch || !liveState) return;
    const starterCount = Object.values(slots).filter(Boolean).length;
    if (starterCount != 11) {
      setSafetyMessage(language === 'pl' ? 'Wybierz dokładnie 11 zawodników.' : 'Select exactly 11 starters.');
      return;
    }

    const label = `${new Date(selectedMatch.match_date).toLocaleDateString()} · ${selectedMatch.opponent || 'Match'}`;
    const saved = await saveLineup({
      name: `Historical XI · ${label}`,
      formation: liveState.formation || '4-3-3',
      slots,
    });

    setLiveState((current) => current ? { ...current, phase: 'full_time', minute: Math.max(90, Number(current.minute || 0)), seconds: 0, running: false } : current);
    setHistoricalEventMinute(0);
    setHistoricalEditMode(true);
    setSafetyMessage(
      saved.ok
        ? (language === 'pl' ? 'Pierwsza XI zapisana w chmurze. Tryb historyczny aktywny.' : 'Starting XI saved to cloud. Historical edit mode active.')
        : (language === 'pl' ? `XI zapisana lokalnie. Chmura: ${saved.message}` : `XI kept locally. Cloud: ${saved.message}`),
    );
    setView('live');
  }

  function applyLatestLineup() {
    const latest = lineups.find((lineup) => lineup.slots && lineup.formation);
    if (!latest) {
      setSafetyMessage(language === 'pl' ? 'Brak zapisanego składu do załadowania.' : 'No saved lineup available.');
      return;
    }
    const latestSlots = (latest.slots || {}) as Record<string, string | null>;
    setSlots(latestSlots);
    syncStartingPerformance(latestSlots);
    if (liveState && latest.formation) updateState({ formation: latest.formation });
    setSafetyMessage(language === 'pl' ? 'Ostatni skład został załadowany.' : 'Latest lineup loaded.');
  }

  const selectedFormationRows = FORMATION_SLOTS[liveState?.formation || '4-3-3'] || FORMATION_SLOTS['4-3-3'];

  const eligibleMotmPlayers = useMemo(() => {
    const ids = new Set<string>();
    Object.values(slots).forEach((id) => { if (id) ids.add(id); });
    Object.values(playerPerformance).forEach((perf) => {
      if (perf.started || perf.on_pitch || perf.minute_off !== null || perf.goals > 0 || perf.assists > 0) ids.add(perf.player_id);
    });
    (selectedMatch?.contributions || []).forEach((entry) => {
      if (Number(entry.minutes || 0) > 0 || Number(entry.goals || 0) > 0 || Number(entry.assists || 0) > 0) ids.add(entry.player_id);
    });
    return players.filter((player) => ids.has(player.id)).sort((a,b) => (a.squad_number ?? 999) - (b.squad_number ?? 999));
  }, [players, slots, playerPerformance, selectedMatch]);

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="pro-match-page">
      <section className="match-command-header metal-card">
        <div>
          <p className="eyebrow">{t.matchOperations}</p>
          <h1>{t.professionalMatchCentre}</h1>
          <p className="muted">{t.professionalMatchSubtitle}</p>
        </div>
        <div className={`sync-indicator ${!online ? 'offline' : cloudStatus}`}>
          {online && cloudStatus === 'synced' ? <Cloud size={18} /> : <CloudOff size={18} />}
          <div>
            <span>
              {!online
                ? t.offlineProtected
                : cloudStatus === 'synced'
                  ? t.cloudSyncedStatus
                  : cloudStatus === 'pending'
                    ? t.localChangesPending
                    : cloudStatus === 'local'
                      ? t.deviceFallback
                      : t.checkingCloud}
            </span>
            <small>
              {lastCloudSync
                ? `${t.lastCloudSave}: ${new Date(lastCloudSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : t.noCloudSaveYet}
            </small>
          </div>
        </div>
      </section>

      <nav className="match-mode-tabs metal-card">
        {([
          ['setup', t.setup],
          ['live', t.liveMatch],
          ['history', t.history],
        ] as Array<[ViewMode, string]>).map(([id, label]) => (
          <button className={view === id ? 'active' : ''} key={id} onClick={() => setView(id)}>
            {label}
          </button>
        ))}
      </nav>

      {view === 'setup' && (
        <>
          <section className="metal-card match-selector-panel">
            <div>
              <label>{t.selectMatch}</label>
              <select value={selectedMatchId} onChange={(event) => { setSelectedMatchId(event.target.value); setHistoricalEditMode(false); }}>
                <option value="">—</option>
                {orderedMatches.map((match) => (
                  <option value={match.id} key={match.id}>
                    {new Date(match.match_date).toLocaleDateString()} · {resolveBundledClub(match.opponent)?.displayName || match.opponent || '—'}
                  </option>
                ))}
              </select>
            </div>
            <button className="gold-button" onClick={() => setShowCreate((value) => !value)}>
              <Plus size={17} /> {t.createMatchday}
            </button>
            <button className="steel-button" onClick={applyLatestLineup}>
              <RefreshCw size={17} /> {t.loadLatestLineup}
            </button>
            <button
              className="match-go-live-button"
              type="button"
              disabled={!selectedMatch}
              onClick={() => setView('live')}
            >
              <Play size={17} /> {t.goToLiveMatch}
            </button>
          </section>

          <section className="metal-card official-fixtures-panel">
            <div className="section-title-line">
              <div>
                <p className="eyebrow">{t.officialGdsfcFixture}</p>
                <h2>{t.premiershipSchedule}</h2>
              </div>
              <span>{POLONIA_2026_27_FIXTURES.length} {t.leagueMatches}</span>
            </div>
            <div className="official-fixture-strip">
              {POLONIA_2026_27_FIXTURES
                .filter((fixture) => new Date(`${fixture.date}T23:59:59`).getTime() >= Date.now() - 86400000)
                .slice(0, 6)
                .map((fixture) => (
                  <button key={fixture.round} onClick={() => useOfficialFixture(fixture.round)}>
                    <ClubLogo club={officialOpponent(fixture)} className="official-fixture-logo" />
                    <span>
                      <b>{t.round} {fixture.round}</b>
                      <strong>{officialOpponent(fixture)}</strong>
                      <small>{new Date(`${fixture.date}T12:00:00`).toLocaleDateString()} · {officialVenueLabel(fixture)}</small>
                      <small>{t.referee}: {fixture.referee || 'TBA'}</small>
                    </span>
                  </button>
                ))}
            </div>
            <p className="fixture-source-note">{t.kickoffRefereeSourceNote}</p>
          </section>

          {showCreate && (
            <section className="metal-card match-create-panel">
              <div>
                <label>{t.date}</label>
                <input type="datetime-local" value={date} onChange={(event) => setDate(event.target.value)} />
              </div>
              <div>
                <label>{t.opponent}</label>
                <select value={opponent} onChange={(event) => setOpponent(event.target.value)}>
                  <option value="">—</option>
                  {PREMIERSHIP_CLUBS.filter((club) => club.key !== 'polonia-glasgow').map((club) => (
                    <option key={club.key} value={club.canonicalName}>{club.displayName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>{t.competition}</label>
                <input value={competition} onChange={(event) => setCompetition(event.target.value)} />
              </div>
              <button className="gold-button" onClick={() => void submitMatch()}>{t.saveMatchday}</button>
              {createMessage && <p className="form-message">{createMessage}</p>}
            </section>
          )}

          {selectedMatch && liveState && (
            <section className="match-setup-grid">
              <article className="metal-card match-identity-card">
                <div className="setup-club">
                  <ClubLogo club="Polonia Glasgow" className="setup-club-logo" />
                  <strong>Polonia Glasgow AFC</strong>
                </div>
                <div className="setup-vs">VS</div>
                <div className="setup-club">
                  <ClubLogo club={selectedMatch.opponent} className="setup-club-logo" />
                  <strong>{resolveBundledClub(selectedMatch.opponent)?.displayName || selectedMatch.opponent}</strong>
                </div>
                <div className="setup-meta">
                  <span><CalendarDays size={16} /> {new Date(selectedMatch.match_date).toLocaleString()}</span>
                  <span><Swords size={16} /> {selectedMatch.competition || '—'}</span>
                </div>
              </article>

              <article className="metal-card setup-details-card">
                <label><MapPin size={16} /> {t.venue}<input value={liveState.venue} onChange={(event) => updateState({ venue: event.target.value })} /></label>
                <label><UserRound size={16} /> {t.referee}<input value={liveState.referee} onChange={(event) => updateState({ referee: event.target.value })} /></label>
                <label><ThermometerSun size={16} /> {t.weather}<input value={liveState.weather} onChange={(event) => updateState({ weather: event.target.value })} /></label>
                <label><Users size={16} /> {t.attendance}<input value={liveState.attendance} onChange={(event) => updateState({ attendance: event.target.value })} /></label>
                <label><Activity size={16} /> {t.formation}
                  <select value={liveState.formation} onChange={(event) => changeHistoricalFormation(event.target.value)}>
                    {Object.keys(FORMATION_SLOTS).map((formation) => <option key={formation}>{formation}</option>)}
                  </select>
                </label>
              </article>

              <article className="metal-card historical-xi-editor">
                <div className="section-title-line historical-xi-title">
                  <div>
                    <p className="eyebrow">{language === 'pl' ? 'SKŁAD MECZOWY' : 'MATCH LINEUP'}</p>
                    <h2>{language === 'pl' ? 'Pierwsza jedenastka' : 'Starting XI'}</h2>
                    <p className="muted">
                      {language === 'pl'
                        ? 'Wybierz zawodnika dla każdej pozycji. Pozostali zawodnicy automatycznie trafiają na ławkę.'
                        : 'Choose one player for every position. All remaining players automatically become the bench.'}
                    </p>
                  </div>
                  <span className="historical-match-badge">
                    {new Date(selectedMatch.match_date).getTime() < Date.now()
                      ? (language === 'pl' ? 'MECZ ARCHIWALNY' : 'HISTORICAL MATCH')
                      : (language === 'pl' ? 'MECZ' : 'MATCH')}
                  </span>
                </div>

                <div className="historical-xi-actions">
                  <button type="button" className="steel-button" onClick={applyLatestLineup}>
                    <RefreshCw size={16} /> {language === 'pl' ? 'Wczytaj ostatni skład' : 'Load latest lineup'}
                  </button>
                  <button type="button" className="steel-button danger-lite" onClick={clearStartingXI}>
                    <X size={16} /> {language === 'pl' ? 'Wyczyść XI' : 'Clear XI'}
                  </button>
                </div>

                <div className="historical-xi-pitch">
                  {selectedFormationRows.map((row, rowIndex) => (
                    <div className="historical-xi-row" key={`${liveState.formation}-${rowIndex}`}>
                      {row.map(slot => {
                        const selectedId = slots[slot] || '';
                        return (
                          <label className="historical-xi-slot" key={slot}>
                            <strong>{slot}</strong>
                            <select value={selectedId} onChange={(event) => assignStartingPlayer(slot, event.target.value)}>
                              <option value="">— {language === 'pl' ? 'wybierz' : 'select'} —</option>
                              {players
                                .filter(player => !Object.entries(slots).some(([otherSlot, id]) => otherSlot !== slot && id === player.id))
                                .map(player => (
                                  <option key={player.id} value={player.id}>
                                    #{player.squad_number ?? '—'} {player.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div className="historical-bench-preview">
                  <strong>{language === 'pl' ? 'Ławka / pozostali' : 'Bench / remaining players'}</strong>
                  <div>
                    {players
                      .filter(player => !Object.values(slots).includes(player.id))
                      .map(player => (
                        <span key={player.id}>#{player.squad_number ?? '—'} {player.name}</span>
                      ))}
                  </div>
                </div>

                <div className="historical-xi-status">
                  <span>
                    {Object.values(slots).filter(Boolean).length}/11 {language === 'pl' ? 'wybranych' : 'selected'}
                  </span>
                  <button type="button" className="gold-button" onClick={() => void confirmHistoricalStartingXI()} disabled={Object.values(slots).filter(Boolean).length !== 11}>
                    <CheckCircle2 size={17} />
                    {language === 'pl' ? 'Zatwierdź XI i przejdź do statystyk' : 'Confirm XI & enter stats'}
                  </button>
                </div>
              </article>
            </section>
          )}
        </>
      )}

      {view === 'live' && selectedMatch && liveState && (
        <>
          <section className="metal-card live-safety-strip">
            <div className="live-safety-title">
              <p className="eyebrow">{t.matchdaySafety}</p>
              <strong>{screenAwake ? t.screenAwakeActive : t.screenAwakeOff}</strong>
            </div>
            <div className="live-safety-actions">
              <button
                type="button"
                className={screenAwake ? 'active' : ''}
                onClick={() => void (screenAwake ? releaseScreenAwake() : requestScreenAwake())}
              >
                <MonitorUp size={17} />
                <span>{screenAwake ? t.allowScreenSleep : t.keepScreenAwake}</span>
              </button>
              <button type="button" onClick={undoLastEvent} disabled={!events.length}>
                <Undo2 size={17} />
                <span>{t.undoLastEvent}</span>
              </button>
            </div>
            {safetyMessage && <p className="live-safety-message">{safetyMessage}</p>}
          </section>

          <section className="metal-card live-scoreboard">
            <div className="live-club-block">
              <ClubLogo club="Polonia Glasgow" className="live-club-logo" />
              <strong>Polonia</strong>
              <div className="score-stepper">
                <button onClick={() => changeScore('home', -1)}>−</button>
                <span>{liveState.home_score}</span>
                <button onClick={() => changeScore('home', 1)}>+</button>
              </div>
            </div>

            <div className={`match-clock-core ${historicalEditMode ? 'historical-clock' : ''}`}>
              <span className={`live-phase ${liveState.running ? 'running' : ''}`}>
                {historicalEditMode ? (language === 'pl' ? 'TRYB HISTORYCZNY · FULL TIME' : 'HISTORICAL EDIT · FULL TIME') : phaseLabel(liveState.phase, t)}
              </span>
              <strong>{liveState.phase === 'full_time' ? `${String(Math.max(90, liveState.minute)).padStart(2, '0')}:00` : `${String(liveState.minute).padStart(2, '0')}:${String(liveState.seconds).padStart(2, '0')}`}</strong>
              {!historicalEditMode && (
                <div className="clock-actions">
                  <button disabled={liveState.phase === 'full_time'} onClick={() => updateState({ running: liveState.phase === 'full_time' ? false : !liveState.running })}>
                    {liveState.running ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                  <button disabled={liveState.phase === 'full_time'} onClick={() => updateState({ running: false, minute: 0, seconds: 0 })}><RefreshCw size={18} /></button>
                </div>
              )}
              {!historicalEditMode ? (
                <select value={liveState.phase} onChange={(event) => updateState({ phase: event.target.value as LiveMatchState['phase'], minute: event.target.value === 'full_time' ? Math.max(90, liveState.minute) : liveState.minute, running: false })}>
                  <option value="not_started">{t.notStarted}</option>
                  <option value="first_half">{t.firstHalf}</option>
                  <option value="half_time">{t.halfTime}</option>
                  <option value="second_half">{t.secondHalf}</option>
                  <option value="full_time">{t.fullTime}</option>
                </select>
              ) : <small>{language === 'pl' ? 'Zegar live jest zablokowany dla zakończonego meczu.' : 'Live clock is locked for completed matches.'}</small>}
            </div>

            <div className="live-club-block">
              <ClubLogo club={selectedMatch.opponent} className="live-club-logo" />
              <strong>{resolveBundledClub(selectedMatch.opponent)?.shortName || selectedMatch.opponent}</strong>
              <div className="score-stepper">
                <button onClick={() => changeScore('away', -1)}>−</button>
                <span>{liveState.away_score}</span>
                <button onClick={() => changeScore('away', 1)}>+</button>
              </div>
            </div>
          </section>

          {historicalEditMode && (
            <section className="metal-card historical-minute-control">
              <div className="historical-minute-heading">
                <div>
                  <p className="eyebrow">{language === 'pl' ? 'HISTORY TIMELINE' : 'HISTORY TIMELINE'}</p>
                  <h2>{language === 'pl' ? 'Wybierz minutę zdarzenia' : 'Select event minute'}</h2>
                  <p className="muted">{language === 'pl' ? 'Każda komenda głosowa, kartka, gol i zmiana zostanie zapisana w wybranej minucie.' : 'Every voice command, card, goal and substitution is stored at the selected minute.'}</p>
                </div>
                <strong>{formatHistoricalMinute(historicalEventMinute)}</strong>
              </div>
              <div className="historical-minute-quick">
                {[0,15,30,45,60,75,90].map((minute) => (
                  <button key={minute} className={historicalEventMinute === minute ? 'active' : ''} onClick={() => setHistoricalMinute(minute)}>{minute}'</button>
                ))}
              </div>
              <div className="historical-minute-editor">
                <button onClick={() => setHistoricalMinute(historicalEventMinute - 1)}>−1</button>
                <input type="range" min="0" max="120" value={historicalEventMinute} onChange={(event) => setHistoricalMinute(Number(event.target.value))} />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  aria-label={language === 'pl' ? 'Minuta zdarzenia' : 'Event minute'}
                  value={historicalEventMinute}
                  onFocus={(event) => { if (event.currentTarget.value === '0') event.currentTarget.value = ''; }}
                  onChange={(event) => setHistoricalMinute(digitsOnlyMinute(event.target.value))}
                />
                <button onClick={() => setHistoricalMinute(historicalEventMinute + 1)}>+1</button>
              </div>
              <small>{language === 'pl' ? 'Dogrywka meczu / doliczony czas: 94 = 90+4.' : 'Stoppage time: enter 94 to record 90+4.'}</small>
            </section>
          )}

          <section className="live-workspace">
            <div className="live-main-column">
              <LiveVoiceLogger players={players} onCommand={recordVoiceCommand} onVoiceSessionStart={() => void requestScreenAwake()} />
              <article className="metal-card quick-event-panel">
                <div className="section-title-line">
                  <div><p className="eyebrow">{t.liveActions}</p><h2>{t.matchEvents}</h2></div>
                  <Clock3 size={25} />
                </div>
                <div className="quick-event-grid">
                  <button onClick={() => addEvent('goal')}><Goal />{t.goal}</button>
                  <button onClick={() => addEvent('yellow')} className="yellow-action"><Square />{t.yellowCard}</button>
                  <button onClick={() => addEvent('red')} className="red-action"><Square />{t.redCard}</button>
                  <button onClick={() => addEvent('substitution')}><ArrowDownUp />{t.substitution}</button>
                  <button onClick={() => addEvent('injury')}><ShieldAlert />{t.injury}</button>
                  <button onClick={() => addEvent('period')}><Clock3 />{t.period}</button>
                </div>
              </article>

              <article className="metal-card timeline-panel">
                <div className="section-title-line">
                  <div><p className="eyebrow">{t.chronology}</p><h2>{t.timeline}</h2></div>
                  <span>{events.length} {t.events}</span>
                </div>
                <div className="match-timeline">
                  {[...events].sort((a, b) => b.minute - a.minute).map((event) => {
                    const Icon = eventIcon(event.event_type);
                    return (
                      <div className={`timeline-event event-${event.event_type}`} key={event.id}>
                        {historicalEditMode ? (
                          <label className="timeline-minute-edit" title={language === 'pl' ? 'Edytuj minutę' : 'Edit minute'}>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              aria-label={language === 'pl' ? 'Edytuj minutę' : 'Edit minute'}
                              value={event.minute}
                              onFocus={(change) => { if (change.currentTarget.value === '0') change.currentTarget.value = ''; }}
                              onChange={(change) => updateEventMinute(event.id, digitsOnlyMinute(change.target.value))}
                            />
                            <span>{formatHistoricalMinute(event.minute)}</span>
                          </label>
                        ) : <span className="timeline-minute">{event.minute}'</span>}
                        <i><Icon size={17} /></i>
                        <div>
                          <strong>{event.title}</strong>
                          {event.detail && <small>{event.detail}</small>}
                        </div>
                        <button onClick={() => setEvents((current) => current.filter((candidate) => candidate.id !== event.id))}>
                          <X size={15} />
                        </button>
                      </div>
                    );
                  })}
                  {!events.length && <p className="muted">{t.noEventsYet}</p>}
                </div>
              </article>

              <LivePlayerPerformancePanel
                players={players}
                matchId={selectedMatch.id}
                teamId={team?.id || ''}
                minute={historicalEditMode ? historicalEventMinute : liveState.minute}
                performance={playerPerformance}
                setPerformance={setPlayerPerformance}
                onSubstitution={recordSubstitution}
              />

              <article className="metal-card live-stats-panel">
                <div className="section-title-line">
                  <div><p className="eyebrow">{t.performanceData}</p><h2>{t.matchStatistics}</h2></div>
                  <Activity size={25} />
                </div>
                <div className="live-stat-editor">
                  {([
                    ['possession', t.possession, '%', 0, 100],
                    ['shots', t.shots, '', 0, 99],
                    ['shots_on_target', t.shotsOnTarget, '', 0, 99],
                    ['corners', t.corners, '', 0, 99],
                    ['fouls', t.fouls, '', 0, 99],
                    ['offsides', t.offsides, '', 0, 99],
                    ['passes', t.passes, '', 0, 999],
                    ['pass_accuracy', t.passAccuracy, '%', 0, 100],
                  ] as Array<[keyof LiveMatchStats, string, string, number, number]>).map(([key, label, suffix, min, max]) => (
                    <label key={key}>
                      <span>{label}</span>
                      <div>
                        <button onClick={() => setStats((current) => ({ ...current, [key]: Math.max(min, current[key] - 1) }))}>−</button>
                        <strong>{stats[key]}{suffix}</strong>
                        <button onClick={() => setStats((current) => ({ ...current, [key]: Math.min(max, current[key] + 1) }))}>+</button>
                      </div>
                    </label>
                  ))}
                </div>
              </article>
            </div>

            <aside className="live-side-column">
              <article className="metal-card live-pitch-panel">
                <div className="section-title-line">
                  <div><p className="eyebrow">{t.onPitch}</p><h2>{liveState.formation}</h2></div>
                  <button className="icon-steel-button" onClick={applyLatestLineup}><RefreshCw size={16} /></button>
                </div>
                <div className="mini-live-pitch">
                  <div className="mini-pitch-center" />
                  {selectedFormationRows.map((row, rowIndex) => (
                    <div className="mini-formation-row" key={rowIndex}>
                      {row.map((slot) => {
                        const player = players.find((candidate) => candidate.id === slots[slot]);
                        return (
                          <button className="live-player-chip" key={slot}>
                            {player?.photo_url
                              ? <img src={player.photo_url} alt="" />
                              : <ClubLogo club="Polonia Glasgow" className="chip-placeholder-logo" />}
                            <span>{player?.squad_number ?? slot}</span>
                            <small>{player?.name?.split(' ').slice(-1)[0] || slot}</small>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </article>

              <article className="metal-card motm-panel">
                <div className="section-title-line">
                  <div><p className="eyebrow">FULL-TIME AWARD</p><h2>{language === 'pl' ? 'MOTM · Zawodnik meczu' : 'MOTM · Man of the Match'}</h2></div>
                  <Star size={25} />
                </div>
                <label>
                  <span>{language === 'pl' ? 'Wybierz zawodnika z meczu' : 'Select from players used in the match'}</span>
                  <select value={motmPlayerId} onChange={(event) => setMotmPlayerId(event.target.value)}>
                    <option value="">{language === 'pl' ? '— wybierz MOTM —' : '— select MOTM —'}</option>
                    {eligibleMotmPlayers.map((player) => {
                      const perf = playerPerformance[player.id];
                      const contribution = selectedMatch.contributions?.find((item) => item.player_id === player.id);
                      const rating = Number(perf?.rating ?? contribution?.rating ?? 0);
                      return <option key={player.id} value={player.id}>#{player.squad_number ?? '—'} · {player.name}{rating ? ` · ${rating.toFixed(1)}` : ''}</option>;
                    })}
                  </select>
                </label>
                {motmPlayerId && (() => {
                  const player = players.find((candidate) => candidate.id === motmPlayerId);
                  const perf = player ? playerPerformance[player.id] : null;
                  const contribution = player ? selectedMatch.contributions?.find((item) => item.player_id === player.id) : null;
                  if (!player) return null;
                  return <div className="motm-preview"><Star size={18}/><strong>#{player.squad_number ?? '—'} {player.name}</strong><span>{Number(perf?.rating ?? contribution?.rating ?? 0).toFixed(1)}</span></div>;
                })()}
                <small>{language === 'pl' ? 'MOTM zostanie zapisany w meczu i automatycznie użyty w grafice Full Time.' : 'MOTM is saved with the match and automatically used by the Full Time public graphic.'}</small>
              </article>

              <article className="metal-card coach-notes-panel">
                <div className="section-title-line">
                  <div><p className="eyebrow">{t.coachPanel}</p><h2>{t.coachNotes}</h2></div>
                  <NotebookPen size={24} />
                </div>
                <textarea value={liveState.coach_notes} onChange={(event) => updateState({ coach_notes: event.target.value })} placeholder={t.coachNotesPlaceholder} />
              </article>

              <button
                className={`sync-live-button ${cloudStatus === 'pending' ? 'pending' : ''}`}
                onClick={() => void syncLiveMatch()}
              >
                <Save size={18} />
                {cloudStatus === 'pending' ? t.syncPendingChanges : t.saveAndSync}
              </button>
              {saveMessage && <p className="sync-message">{saveMessage}</p>}
            </aside>
          </section>

          {activeEventType && !['period', 'note'].includes(activeEventType) && (
            <div className="event-modal-backdrop">
              <section className="metal-card event-modal">
                <button className="modal-close" onClick={() => setActiveEventType(null)}><X /></button>
                <p className="eyebrow">{t.addEvent}</p>
                <h2>{activeEventType === 'goal' ? t.goal : activeEventType === 'yellow' ? t.yellowCard : activeEventType === 'red' ? t.redCard : activeEventType === 'substitution' ? t.substitution : t.injury}</h2>
                <label>
                  <span>{activeEventType === 'substitution' ? t.playerOut : t.player}</span>
                  <select value={eventPlayer} onChange={(event) => setEventPlayer(event.target.value)}>
                    <option value="">—</option>
                    {players.map((player) => <option key={player.id} value={player.id}>#{player.squad_number ?? '—'} {player.name}</option>)}
                  </select>
                </label>
                {activeEventType === 'substitution' && (
                  <label>
                    <span>{t.playerIn}</span>
                    <select value={eventSecondaryPlayer} onChange={(event) => setEventSecondaryPlayer(event.target.value)}>
                      <option value="">—</option>
                      {players.map((player) => <option key={player.id} value={player.id}>#{player.squad_number ?? '—'} {player.name}</option>)}
                    </select>
                  </label>
                )}
                <label>
                  <span>{t.detail}</span>
                  <input value={eventDetail} onChange={(event) => setEventDetail(event.target.value)} />
                </label>
                <button className="gold-button" onClick={confirmPlayerEvent}>{t.addEvent}</button>
              </section>
            </div>
          )}
        </>
      )}

      {view === 'history' && (
        <section className="match-history-grid">
          {orderedMatches.map((match) => {
            const completed = match.goals_for !== null && match.goals_against !== null;
            return (
              <button
                className="metal-card history-match-card"
                key={match.id}
                onClick={() => {
                  setSelectedMatchId(match.id);
                  setHistoricalEditMode(true);
                  setView('setup');
                  window.setTimeout(() => {
                    document.querySelector('.historical-xi-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 120);
                }}
              >
                <div>
                  <span>{new Date(match.match_date).toLocaleDateString()}</span>
                  <small>{match.competition || '—'}</small>
                </div>
                <div className="history-clubs">
                  <ClubLogo club="Polonia Glasgow" className="history-logo" />
                  <strong>{completed ? `${match.goals_for} : ${match.goals_against}` : 'VS'}</strong>
                  <ClubLogo club={match.opponent} className="history-logo" />
                </div>
                <div className="history-match-action">
                  <div className="history-opponent-copy">
                    <strong>{resolveBundledClub(match.opponent)?.displayName || match.opponent || '—'}</strong>
                    <small>{language === 'pl' ? 'Edytuj XI i statystyki' : 'Edit XI & stats'}</small>
                  </div>
                  <ChevronRight className="history-match-chevron" size={18} />
                </div>
              </button>
            );
          })}
          {!orderedMatches.length && <section className="metal-card empty-panel"><Swords /><p>{t.noMatches}</p></section>}
        </section>
      )}
    </div>
  );
}
